import React, { useState, useEffect } from 'react';
import { AppData, StoreType } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { Globe, ArrowUp, Minus, ArrowRight, Loader2, Info } from 'lucide-react';
import { getGrowthClassification } from '../components/GrowthScoreGauge';
import { appCatalog, STOREFRONT_COUNTRIES } from '../services/appCatalogService';
import { CATEGORIES_LIST } from '../data/mockData';

interface ChartsViewProps {
  onSelectApp: (appId: string) => void;
  onCompare: (appIds: string[]) => void;
}

export const ChartsView: React.FC<ChartsViewProps> = ({ onSelectApp, onCompare }) => {
  const [selectedCountry, setSelectedCountry] = useState(appCatalog.getSelectedCountry());
  const [selectedStore, setSelectedStore] = useState<StoreType>('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [chartType, setChartType] = useState<'free' | 'paid' | 'grossing'>('free');
  const [apps, setApps] = useState<AppData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const sortParam = chartType === 'grossing' ? 'trending' : 'top_ranked';
    appCatalog.searchApps({
      country: selectedCountry,
      category: selectedCategory,
      store: selectedStore,
      sort: sortParam as any,
      limit: 50
    }).then((res) => {
      if (isMounted) {
        let sorted = res.results || [];
        if (chartType === 'grossing') {
          sorted = [...sorted].sort((a, b) => b.estimatedMonthlyRevenueUsd - a.estimatedMonthlyRevenueUsd);
        }
        setApps(sorted);
        setIsLoading(false);
      }
    }).catch(() => {
      if (isMounted) setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedCountry, selectedStore, selectedCategory, chartType]);

  const activeCountry = STOREFRONT_COUNTRIES.find((c) => c.code === selectedCountry) || STOREFRONT_COUNTRIES[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Globe className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Storefront Discovery Leaderboards</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            Top Charts & Discovery — {activeCountry.name} {activeCountry.flag}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Real-time observed applications across categories, stores, and estimated monetization models.
          </p>
        </div>

        {/* Chart Type Toggle */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1.5 rounded-2xl text-xs font-semibold self-start lg:self-auto">
          {(['free', 'paid', 'grossing'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setChartType(t)}
              className={`px-3.5 py-1.5 rounded-xl transition-all capitalize cursor-pointer ${
                chartType === t
                  ? 'bg-white text-blue-600 shadow-sm font-bold'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              {t === 'grossing' ? 'Top Grossing' : `Top ${t}`}
            </button>
          ))}
        </div>
      </div>

      {/* Semantic Notice Banner */}
      <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex items-center gap-3 text-xs text-slate-600">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <span>
          <strong>Ranking Provenance:</strong> Leaderboard positions reflect verified public search observations and estimated velocity models. Official Apple Chart RSS integration activates when a commercial chart provider is configured.
        </span>
      </div>

      {/* Filter Controls Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Country Filter */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
            Storefront Country
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              appCatalog.setSelectedCountry(e.target.value);
            }}
            className="w-full text-xs font-semibold bg-white border border-slate-200 text-slate-800 rounded-2xl px-3.5 py-2.5 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          >
            {STOREFRONT_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.name} ({c.code})
              </option>
            ))}
          </select>
        </div>

        {/* Store Filter */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
            Store
          </label>
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value as any)}
            className="w-full text-xs font-semibold bg-white border border-slate-200 text-slate-800 rounded-2xl px-3.5 py-2.5 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          >
            <option value="all">All Stores</option>
            <option value="appstore">Apple App Store (Primary)</option>
            <option value="googleplay">Google Play Store</option>
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
            Category
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full text-xs font-semibold bg-white border border-slate-200 text-slate-800 rounded-2xl px-3.5 py-2.5 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          >
            <option value="all">All Categories</option>
            {CATEGORIES_LIST.filter((c) => c.slug !== 'all').map((cat) => (
              <option key={cat.slug} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Top Leaderboard Table */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-xs text-slate-500 font-medium">Querying catalog rankings for {activeCountry.name}...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                  <th className="pb-3 pl-2 w-16 text-center">Pos</th>
                  <th className="pb-3">Application & Developer</th>
                  <th className="pb-3">Category</th>
                  <th className="pb-3 text-center">Observed Signal</th>
                  <th className="pb-3 text-center">Growth Score</th>
                  <th className="pb-3 text-center">Est. Monthly DL</th>
                  <th className="pb-3 text-right pr-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {apps.map((app, idx) => {
                  const rankNum = idx + 1;
                  const { label, bg } = getGrowthClassification(app.growthScore);

                  return (
                    <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Pos */}
                      <td className="py-4 pl-2 text-center">
                        <span
                          className={`inline-flex items-center justify-center font-black text-sm w-8 h-8 rounded-xl ${
                            rankNum === 1
                              ? 'bg-amber-100 text-amber-900 shadow-2xs'
                              : rankNum === 2
                              ? 'bg-slate-200 text-slate-800'
                              : rankNum === 3
                              ? 'bg-amber-50 text-amber-800'
                              : 'text-slate-700 font-bold'
                          }`}
                        >
                          {rankNum}
                        </span>
                      </td>

                      {/* App */}
                      <td className="py-4">
                        <div
                          onClick={() => onSelectApp(app.id)}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <AppIcon
                            app={app}
                            size="md"
                            className="w-11 h-11 rounded-2xl shadow-sm group-hover:scale-105 transition-transform flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">
                                {app.name}
                              </span>
                              <StoreBadge store={app.primaryStore} />
                            </div>
                            <span className="text-xs text-slate-400 block">{app.developer}</span>
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="py-4 font-medium text-slate-600">
                        {app.category}
                      </td>

                      {/* Movement / Search Pos */}
                      <td className="py-4 text-center">
                        <span className="inline-flex items-center gap-0.5 text-blue-600 font-bold text-xs bg-blue-50 px-2 py-0.5 rounded-md">
                          Search Pos #{app.currentRank}
                        </span>
                      </td>

                      {/* Growth Score */}
                      <td className="py-4 text-center">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${bg}`}>
                          {app.growthScore} • {label}
                        </span>
                      </td>

                      {/* Estimated Downloads */}
                      <td className="py-4 text-center font-semibold text-slate-700">
                        {app.estimatedMonthlyDownloads > 1000000
                          ? `${(app.estimatedMonthlyDownloads / 1000000).toFixed(1)}M`
                          : `${(app.estimatedMonthlyDownloads / 1000).toFixed(0)}K`} <small className="text-[10px] text-slate-400 font-normal">est</small>
                      </td>

                      {/* Action */}
                      <td className="py-4 text-right pr-2">
                        <button
                          onClick={() => onSelectApp(app.id)}
                          className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-xl transition-colors inline-flex items-center gap-1 cursor-pointer"
                        >
                          <span>View Stats</span>
                          <ArrowRight className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
